import React from 'react';
import { useTranslation } from 'react-i18next';
import DocsLayout, {
  NavSection
} from '../../common/components/docs-layout.component';

// To add a new doc page: add it to the relevant section's items array and register the route in navigation/index.tsx.
// To add a group: omit `to` and use `children` instead of `subItems`.
// To add sub-anchors to a page: add `subItems` with anchor ids matching id="" on the page.
const TouchposeDocsLayout: React.FC = () => {
  const { t } = useTranslation();

  const NAV_SECTIONS: NavSection[] = [
    {
      title: t('docs.layout.sectionTitle'),
      items: [
        {
          label: t('docs.layout.overview'),
          to: '/touchpose/docs'
        },
        {
          label: t('docs.layout.gettingStarted'),
          children: [
            {
              label: t('docs.layout.getTouchPose'),
              to: '/touchpose/docs/get-touchpose'
            },
            {
              label: t('docs.layout.installationGuide'),
              to: '/touchpose/docs/getting-started'
            },
            {
              label: t('docs.layout.loadingTouchFiles'),
              to: '/touchpose/docs/loading-touchfile'
            }
          ]
        },
        {
          label: t('docs.layout.setupAndConfiguration'),
          children: [
            {
              label: t('docs.layout.mayaMenu'),
              to: '/touchpose/docs/maya-menu'
            },
            {
              label: t('docs.layout.editor'),
              to: '/touchpose/docs/editor',
              subItems: [
                { label: t('docs.layout.editorSubOverview'), anchor: 'overview' },
                {
                  label: t('docs.layout.touchSetEditor'),
                  anchor: 'touch-set-editor'
                },
                { label: t('docs.layout.layers'), anchor: 'layers' },
                { label: t('docs.layout.groups'), anchor: 'groups' },
                {
                  label: t('docs.layout.touchposeColors'),
                  anchor: 'touchpose-colors'
                },
                { label: t('docs.layout.preferences'), anchor: 'preferences' }
              ]
            },
            {
              label: t('docs.layout.importExportTouch'),
              to: '/touchpose/docs/loading-touchsets',
              subItems: [
                { label: t('docs.layout.importing'), anchor: 'import-touch' },
                { label: t('docs.layout.exporting'), anchor: 'export-touch' }
              ]
            },
            {
              label: t('docs.layout.actions'),
              to: '/touchpose/docs/actions',
              subItems: [
                { label: t('docs.layout.eventTypes'), anchor: 'event-types' },
                {
                  label: t('docs.layout.managingActions'),
                  anchor: 'managing-actions'
                },
                { label: t('docs.layout.codingActions'), anchor: 'coding-actions' },
                {
                  label: t('docs.layout.selectionBuffer'),
                  anchor: 'selection-buffer'
                },
                { label: t('docs.layout.modifierKeys'), anchor: 'modifier-keys' },
                { label: t('docs.layout.hotkeys'), anchor: 'hotkeys' }
              ]
            },
            {
              label: t('docs.layout.actionMenus'),
              to: '/touchpose/docs/actions-menus',
              subItems: [
                { label: t('docs.layout.menusRadialBasics'), anchor: 'radial-menus' },
                { label: t('docs.layout.menusMinimal'), anchor: 'minimal-example' },
                {
                  label: t('docs.layout.menusPositions'),
                  anchor: 'position-conventions'
                },
                { label: t('docs.layout.menusSpace'), anchor: 'space-switching' },
                { label: t('docs.layout.menusIkFk'), anchor: 'ikfk-switching' },
                { label: t('docs.layout.menusFull'), anchor: 'full-example' }
              ]
            },
            {
              label: t('docs.layout.drawGroups'),
              to: '/touchpose/docs/draw-groups',
              subItems: [
                { label: t('docs.layout.drawGroupsOverview'), anchor: 'overview' },
                { label: t('docs.layout.drawGroupsTab'), anchor: 'draw-tab' },
                {
                  label: t('docs.layout.drawGroupsGroupProps'),
                  anchor: 'group-properties'
                },
                {
                  label: t('docs.layout.drawGroupsMemberProps'),
                  anchor: 'member-properties'
                },
                { label: t('docs.layout.drawGroupsApplyOrder'), anchor: 'apply-order' },
                {
                  label: t('docs.layout.drawGroupsMirroring'),
                  anchor: 'mirroring-draw-groups'
                },
                {
                  label: t('docs.layout.drawGroupsLayersSave'),
                  anchor: 'layers-and-save'
                }
              ]
            },
            {
              label: t('docs.layout.preferences'),
              to: '/touchpose/docs/preferences',
              subItems: [
                { label: t('docs.layout.preferencesGlobal'), anchor: 'global' },
                { label: t('docs.layout.preferencesEditor'), anchor: 'editor' }
              ]
            },
            {
              label: t('docs.layout.unreal'),
              to: '/touchpose/docs/unreal',
              subItems: [
                { label: t('docs.layout.unrealOverview'), anchor: 'overview' },
                {
                  label: t('docs.layout.unrealRequirements'),
                  anchor: 'requirements'
                },
                { label: t('docs.layout.unrealInstalling'), anchor: 'installing' },
                { label: t('docs.layout.unrealMapping'), anchor: 'mapping' },
                { label: t('docs.layout.unrealImport'), anchor: 'import-dmc' },
                { label: t('docs.layout.unrealExport'), anchor: 'export-touch' },
                {
                  label: t('docs.layout.unrealTroubleshooting'),
                  anchor: 'troubleshooting'
                }
              ]
            }
          ]
        },
        {
          label: t('docs.layout.forAnimators'),
          children: [
            {
              label: t('docs.layout.animatingWithTouchPose'),
              to: '/touchpose/docs/animating',
              subItems: [
                { label: t('docs.layout.enablingTouchPose'), anchor: 'enabling' },
                { label: t('docs.layout.whatYouSee'), anchor: 'the-overlay' },
                { label: t('docs.layout.adjustingOverlay'), anchor: 'settings' },
                {
                  label: t('docs.layout.referencedScenes'),
                  anchor: 'referenced-scenes'
                }
              ]
            },
            {
              label: t('docs.layout.drawingPoses'),
              to: '/touchpose/docs/drawing-poses',
              subItems: [
                { label: t('docs.layout.drawingPosesOverview'), anchor: 'overview' },
                { label: t('docs.layout.drawingPosesActivating'), anchor: 'activating' },
                {
                  label: t('docs.layout.drawingPosesSketch'),
                  anchor: 'sketch-and-pose'
                },
                {
                  label: t('docs.layout.drawingPosesMode'),
                  anchor: 'choose-and-sketch-mode'
                },
                {
                  label: t('docs.layout.drawingPosesToolSettings'),
                  anchor: 'tool-settings'
                },
                { label: t('docs.layout.drawingPosesHotkeys'), anchor: 'hotkeys' },
                {
                  label: t('docs.layout.drawingPosesReferenced'),
                  anchor: 'referenced-rigs'
                }
              ]
            }
          ]
        },
        {
          label: t('docs.layout.pythonApi'),
          children: [
            {
              label: t('docs.layout.apiReference'),
              to: '/touchpose/docs/api',
              subItems: [
                { label: t('docs.layout.apiExporting'), anchor: 'export' },
                { label: t('docs.layout.apiImporting'), anchor: 'import' },
                { label: t('docs.layout.apiTouchSets'), anchor: 'touch-sets' },
                { label: t('docs.layout.apiActions'), anchor: 'actions' },
                {
                  label: t('docs.layout.apiDrawPoseEntry'),
                  anchor: 'draw-pose-entry'
                },
                {
                  label: t('docs.layout.apiRenameControl'),
                  anchor: 'rename-control'
                },
                {
                  label: t('docs.layout.apiSetActiveLayer'),
                  anchor: 'set-active-layer'
                }
              ]
            }
          ]
        }
      ]
    }
  ];

  return <DocsLayout navSections={NAV_SECTIONS} />;
};

export default TouchposeDocsLayout;
